import { openDB } from 'idb';
import { isLocalMirror } from '../config/constants';

const DB_NAME = 'myAppDB';
const DB_VERSION = 2;
const STORE_NAME = 'menuData';
const IMAGE_STORE_NAME = 'menuImages';

export const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' }); 
      }
      if (!db.objectStoreNames.contains(IMAGE_STORE_NAME)) {
        db.createObjectStore(IMAGE_STORE_NAME, { keyPath: 'id' }); // Almacén para imágenes
      }
    },
  });
};

export const saveData = async (key, value) => {
  try {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.store.put({ id: key, value });
    await tx.done;
    } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.log('No se pudo almacenar el blob: límite de almacenamiento alcanzado.');
    } else {
      console.log('Error al almacenar el blob:', error);
    }
  }
};


export const saveImage = async (id, data) => {

  try {
    const db = await initDB();
    const tx = db.transaction(IMAGE_STORE_NAME, 'readwrite');
    tx.store.put({ id, data });
    await tx.done;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.error('No se pudo almacenar el blob: límite de almacenamiento alcanzado.');
    } else {
      console.error('Error al almacenar el blob:', error);
    }
  }
};

export const getImage = async (id) => {
  const db = await initDB();
  return db.get(IMAGE_STORE_NAME, id);
};

export const getData = async (key) => {
  const db = await initDB();
  return db.get(STORE_NAME, key);
};

export const clearData = async () => {
  const db = await initDB();
  const tx = db.transaction(STORE_NAME, 'readwrite');
  tx.store.clear();
  await tx.done;
};

/**
 * Guarda solo la RUTA de una imagen, sin el binario.
 *
 * En el kiosko el archivo ya está en el disco del iPad y lo sirve el servidor
 * local, así que duplicarlo como blob en IndexedDB solo gasta cuota — que es
 * justo el límite con el que veníamos chocando (QuotaExceededError).
 */
export const saveImagePath = async (id, path, changeimage) => {
  try {
    const db = await initDB();
    const tx = db.transaction(IMAGE_STORE_NAME, 'readwrite');
    tx.store.put({ id, data: { path, changeimage } });
    await tx.done;
  } catch (error) {
    console.error('Error al almacenar la ruta de la imagen:', error);
  }
};

/**
 * Devuelve algo que se pueda poner directo en un <img src>.
 *
 * - Espejo local: la ruta tal cual. Sin blobs, sin createObjectURL y por tanto
 *   sin la fuga de memoria que crecía todo el día en los iPads.
 * - Navegador normal: la URL de objeto del blob, como siempre.
 */
export const resolveImage = async (id) => {
  // Espejo local: la ruta sale del JSON del menú, no de IndexedDB.
  if (isLocalMirror) {
    return mapaImagenes.get(id) ?? null;
  }

  const record = await getImage(id);
  if (!record || !record.data) return null;

  if (record.data.path) return record.data.path;
  if (record.data.blob) return URL.createObjectURL(record.data.blob);

  return null;
};

export { isLocalMirror };

/* ------------------------------------------------------------------------
 * Mapa de imágenes en memoria (solo espejo local)
 *
 * En el kiosko las fotos ya están en el disco del iPad y las sirve el
 * servidor local, así que la ruta de cada una se puede DERIVAR del mismo JSON
 * del menú que pintan las pantallas.
 *
 * Antes se copiaban a IndexedDB, y esa copia era una segunda fuente de verdad
 * que podía quedarse vieja: el texto del plato cambiaba (viene del JSON) pero
 * la foto no (venía de IndexedDB). Derivándola del JSON eso es imposible por
 * construcción: si se ve el texto nuevo, se ve la foto nueva.
 * ---------------------------------------------------------------------- */

let mapaImagenes = new Map();

/** Reconstruye el mapa a partir del JSON del menú. Se llama en cada carga. */
export const construirMapaImagenes = (data, paths) => {
  const mapa = new Map();

  (data?.data ?? []).forEach((categoria) => {
    if (categoria.icon) {
      mapa.set(`menu-cat-${categoria.id}`, `${paths.categorias}/${categoria.icon}`);
    }

    (categoria.menus ?? []).forEach((plato) => {
      const campos = [
        ['menu-image-', plato.image],
        ['menu-image-h-', plato.imagehorizontal],
        ['menu-image-one-', plato.image1],
        ['menu-image-two-', plato.image2],
      ];
      campos.forEach(([prefijo, archivo]) => {
        if (archivo) mapa.set(`${prefijo}${plato.id}`, `${paths.menus}/${archivo}`);
      });
    });
  });

  mapaImagenes = mapa;
  return mapa.size;
};
